import { useState } from 'react';
import { UserPlus, UserCheck, Crown } from 'lucide-react';
import { useUserLibrary } from '../contexts/UserLibraryContext';
import { useAuth } from '../contexts/AuthContext';
import { effectiveTier } from '../utils/entitlements';
import { useToast } from './ui/Toast';
import { SocialUpgradeModal } from './ui/SocialUpgradeModal';

interface FollowButtonProps {
  userId: string;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * FollowButton - Follow/Following toggle button
 *
 * Behavior:
 * - Shows "Follow" when not following
 * - Shows "Following" when following (hover shows "Unfollow")
 * - Parent component handles "Follows you" text display separately
 * - Uses optimistic UI from UserLibraryContext
 * - NO loading spinner per spec
 * - Always renders (parent hides if needed for own profile)
 */
export function FollowButton({
  userId,
  variant = 'default',
  size = 'md',
  className = '',
}: FollowButtonProps) {
  const { isFollowing, toggleFollow } = useUserLibrary();
  const { profile } = useAuth();
  const { toast } = useToast();
  const [isHovered, setIsHovered] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const isFree = !!profile && effectiveTier(profile) === 'free';
  const following = isFollowing(userId);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isFree) {
      setShowUpgradeModal(true);
      return;
    }

    // Capture state before toggle for toast message
    const wasFollowing = following;

    const result = await toggleFollow(userId);

    if (result === 'SOCIAL_TIER_REQUIRED') {
      setShowUpgradeModal(true);
    } else if (result) {
      // Success - mutationId returned
      toast.success(wasFollowing ? 'Unfollowed' : 'Followed');
    } else {
      // Failure - null returned
      toast.error(wasFollowing ? 'Failed to unfollow' : 'Failed to follow');
    }
  };

  // Size classes
  const sizeClasses = {
    sm: 'px-3 py-1 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-2.5 text-base',
  };

  // Variant classes
  const variantClasses = {
    default: following
      ? 'bg-slate-700 hover:bg-red-600 text-white border border-slate-600 hover:border-red-600'
      : 'bg-teal-600 hover:bg-teal-700 text-white',
    outline: following
      ? 'bg-transparent hover:bg-red-600/10 text-slate-300 border border-slate-600 hover:border-red-500 hover:text-red-400'
      : 'bg-transparent hover:bg-teal-600/10 text-teal-400 border border-teal-600 hover:border-teal-500',
    ghost: following
      ? 'bg-transparent hover:bg-red-600/10 text-slate-300 hover:text-red-400'
      : 'bg-transparent hover:bg-teal-600/10 text-teal-400',
  };

  // Display text: "Follow" / "Following" / "Unfollow" (on hover when following)
  const displayText = isFree
    ? 'Follow'
    : following
      ? isHovered
        ? 'Unfollow'
        : 'Following'
      : 'Follow';

  const Icon = isFree ? Crown : following ? UserCheck : UserPlus;

  return (
    <>
      <button
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title={isFree ? 'Upgrade to Social to follow users' : undefined}
        className={`
          inline-flex items-center justify-center gap-2 rounded-lg font-medium
          transition-all duration-200 ease-in-out
          focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-slate-900
          ${sizeClasses[size]}
          ${isFree ? 'bg-slate-700 text-slate-400 border border-slate-600 opacity-75 hover:opacity-100 transition-opacity' : variantClasses[variant]}
          ${className}
        `}
      >
        <Icon
          className={size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'}
        />
        <span className="min-w-[4.5rem] text-center">{displayText}</span>
      </button>

      <SocialUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="Following people"
      />
    </>
  );
}
